/**
 * Envia heartbeat a cada 60 segundos.
 * - Usa `needsSync` da resposta para forçar refetch de schedule
 * - Popula workspaceId/name nas credenciais no primeiro beat bem-sucedido
 * - Reporta o item em reprodução (playlistId + assetId) ao servidor
 */
import * as Sentry from '@sentry/react'
import { useEffect, useRef } from 'react'
import { assetCache } from '@/engine/asset-cache'
import { createCommandExecutor } from '@/engine/command-executor'
import { playLogManager } from '@/engine/play-log-manager'
import { env } from '@/env'
import { deviceService } from '@/services/device.service'
import { useAuthStore } from '@/store/auth.store'
import { usePlayerStore } from '@/store/player.store'
import { playerLogger } from '@/utils/player-logger'

export interface NowPlaying {
  playlistId?: string
  assetId?: string
}

const HEARTBEAT_INTERVAL_MS = 60_000

type RefetchCallback = () => void
let onShouldRefetch: RefetchCallback | null = null

// Flag para garantir que o primeiro sync só aconteça uma vez, mesmo com Strict Mode
let isFirstBeatFired = false

/** Comandos remotos entregues no heartbeat (Onda 2). Módulo: o "já executado" sobrevive a remounts. */
const commandExecutor = createCommandExecutor({
  ack: async (commandId, ack) => {
    const creds = useAuthStore.getState().credentials
    if (!creds) throw new Error('no credentials')
    await deviceService.ackCommand(creds.deviceId, commandId, ack)
  },
  refetch: () => onShouldRefetch?.(),
  reload: () => window.location.reload(),
  clearCache: () => assetCache.clearPersistent(),
  recentLogs: () => playerLogger.recent()
})

/**
 * Registra o callback de refetch forçado e devolve a função que o remove (CLAUDE.md 2.1, L-14).
 * O unregister só limpa se o callback ainda for o registrado — um remount (Strict Mode) que já
 * registrou outro não é desfeito pelo cleanup do anterior.
 */
export function registerRefetchCallback(cb: RefetchCallback): () => void {
  onShouldRefetch = cb
  return () => {
    if (onShouldRefetch === cb) onShouldRefetch = null
  }
}

export function useHeartbeat(nowPlaying?: NowPlaying | null): void {
  const nowPlayingRef = useRef(nowPlaying)
  nowPlayingRef.current = nowPlaying

  useEffect(() => {
    const initialCreds = useAuthStore.getState().credentials
    if (!initialCreds) return

    // Identifica o device no Sentry para todos os erros capturados nesta sessão
    Sentry.setUser({ id: initialCreds.deviceId })
    Sentry.setTag('player_release', env.VITE_PLAYER_RELEASE)

    let stopped = false

    async function beat(): Promise<void> {
      if (stopped) return
      const creds = useAuthStore.getState().credentials
      if (!creds) return

      const [cacheSize, pendingLogs, deadLetterCount] = await Promise.all([
        assetCache.getCacheSize(),
        playLogManager.getPendingCount(),
        playLogManager.getDeadLetterCount()
      ])

      const mem = (performance as { memory?: { usedJSHeapSize: number } }).memory

      const payload: Parameters<typeof deviceService.heartbeat>[1] = {
        playerRelease: env.VITE_PLAYER_RELEASE,
        ...(nowPlayingRef.current?.playlistId && { currentPlaylistId: nowPlayingRef.current.playlistId }),
        ...(nowPlayingRef.current?.assetId && { currentAssetId: nowPlayingRef.current.assetId }),
        storageCacheUsedMb: Math.round(cacheSize / 1024 / 1024),
        pendingPlayLogsCount: pendingLogs,
        playlogDeadLetterCount: deadLetterCount,
        resolutionWidth: window.screen.width,
        resolutionHeight: window.screen.height,
        ...(mem ? { memoryUsedMb: Math.round(mem.usedJSHeapSize / 1024 / 1024) } : {})
      }

      try {
        const res = await deviceService.heartbeat(creds.deviceId, payload)

        usePlayerStore.getState().setOffline(false)

        let shouldSync = false

        // Primeiro beat BEM SUCEDIDO da história da aplicação: sempre sincronizar
        if (!isFirstBeatFired) {
          shouldSync = true
          isFirstBeatFired = true
        }

        // Primeira vez que temos workspaceId: preencher credenciais e sincronizar
        if (res.workspaceId && !creds.workspaceId) {
          useAuthStore.getState().setCredentials({
            ...creds,
            workspaceId: res.workspaceId,
            workspaceName: res.name ?? creds.workspaceName,
            workspaceTimezone: res.workspaceTimezone ?? creds.workspaceTimezone
          })
          Sentry.setTag('workspace_id', res.workspaceId)
          shouldSync = true
        } else if (res.workspaceTimezone && res.workspaceTimezone !== creds.workspaceTimezone) {
          // Fuso vem do workspace na API; sem isto o player fica em UTC e o dayparting (HH:MM) erra
          useAuthStore.getState().setCredentials({
            ...creds,
            workspaceTimezone: res.workspaceTimezone
          })
          shouldSync = true
        }

        // Servidor pode forçar um refetch a qualquer momento
        if (res.needsSync) {
          shouldSync = true
        }

        // Fora do caminho do heartbeat: comando lento (limpar cache) não atrasa o próximo beat.
        void commandExecutor.retryPendingAcks().then(() => commandExecutor.run(res.commands ?? []))

        if (shouldSync) {
          onShouldRefetch?.()
        }

        // operatingHoursAction é opcional (pode não vir na versão atual da API)
        const action = res.operatingHoursAction
        if (action === 'black_screen') {
          usePlayerStore.getState().setScreen('standby')
        } else if (!action || action === 'none') {
          if (usePlayerStore.getState().screen === 'standby') {
            usePlayerStore.getState().setScreen('playing')
          }
        }
      } catch {
        usePlayerStore.getState().setOffline(true)
      }
    }

    void beat()
    const interval = setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS)

    return () => {
      stopped = true
      clearInterval(interval)
    }
  }, [])
}
